import { useLibraryPanel } from '../components/chat/LibraryPanel';
import { libraryTarget, citedSubPart } from '../components/chat/libraryLinks';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { attachClassificationBridge } from './bridge';
import { ClassificationContext, classificationParentPath } from './transport';
import { classificationDocument, classificationSelection } from './navigation';
import ShareDealDialog from './ShareDealDialog';
import { pendingRequests, checkSavedRequest, stopSavedRequest, SavedRequest } from './recovery';

export default function ClassificationFrame({ base, context, referenceWidth = 0, onAgentWidth }: { base: string; context: ClassificationContext; referenceWidth?: number; onAgentWidth?: (width: number) => void }) {
  const library = useLibraryPanel();
  const latestReferenceWidth = React.useRef(referenceWidth);
  latestReferenceWidth.current = referenceWidth;
  const bridge = React.useRef<ReturnType<typeof attachClassificationBridge>>();
  React.useEffect(() => { bridge.current?.setReferenceWidth(referenceWidth); }, [referenceWidth]);
  const projectId = context.project_id;
  const frame = React.useRef<HTMLIFrameElement>(null);
  const actual = React.useRef<string | null>(null);
  const connectionTimer = React.useRef<number>();
  const location = useLocation();
  const navigate = useNavigate();
  const selection = classificationSelection(location.search);
  const latestSelection = React.useRef(selection);
  latestSelection.current = selection;
  const [failure, setFailure] = React.useState<string | null>(null);
  const [ready, setReady] = React.useState(false);
  const [attempt, retry] = React.useReducer(value => value + 1, 0);
  const [recoveryVersion, refreshRecovery] = React.useReducer(value => value + 1, 0);
  const [savedRequests, setSavedRequests] = React.useState<SavedRequest[]>([]);
  const [recoveryMessage, setRecoveryMessage] = React.useState('');
  const [recoveryBusy, setRecoveryBusy] = React.useState(false);
  const [recoveryOpen, setRecoveryOpen] = React.useState(false);
  const [sharingRoot, setSharingRoot] = React.useState<string>();
  const recoveryController = React.useRef<AbortController>();
  React.useEffect(() => {
    const controller = new AbortController();
    recoveryController.current = controller;
    setRecoveryBusy(false);
    pendingRequests(base, context, controller.signal).then(rows => {
      if (!controller.signal.aborted) setSavedRequests(rows);
    }).catch(error => { if (!controller.signal.aborted) setRecoveryMessage(error.message); });
    return () => controller.abort();
  }, [base, context, recoveryVersion]);
  const recover = async (saved: SavedRequest, stop: boolean) => {
    const controller = recoveryController.current;
    if (!controller || controller.signal.aborted) return;
    setRecoveryBusy(true);
    try {
      const current = await (stop ? stopSavedRequest : checkSavedRequest)(base, context, saved, controller.signal);
      if (controller.signal.aborted) return;
      if (['completed', 'rejected'].includes(current.phase)) {
        setSavedRequests(rows => rows.filter(row => row.command_id !== saved.command_id));
        setRecoveryMessage('Saved outcome verified. Reload the saved view or open its history to inspect the result and any completed edits.');
      } else {
        setSavedRequests(rows => rows.map(row => row.command_id === saved.command_id ? current : row));
        setRecoveryMessage('The saved request is unfinished. Checking it does not start or retry model work.');
      }
    } catch (error) { if (!controller.signal.aborted) setRecoveryMessage(error instanceof Error ? error.message : 'The saved request is unavailable.'); }
    finally { if (!controller.signal.aborted) setRecoveryBusy(false); }
  };
  React.useEffect(() => {
    const element = frame.current;
    if (!element) return;
    setReady(false); setFailure(null); actual.current = null;
    if (new URL(base).origin === window.location.origin) {
      setFailure('Classification requires its isolated application origin in this environment.');
      return;
    }
    const detach = attachClassificationBridge({ frame: element, base, context,
      onCommandStateChange: refreshRecovery, libraryAvailable: Boolean(library),
      referenceWidth: () => latestReferenceWidth.current, onAgentWidth,
      onShellAction: action => {
        if (action.action === 'open-library') {
          const target = library && libraryTarget(action.href, library.origin, citedSubPart(action.locator, libraryTarget(action.href, library.origin)?.article));
          if (target && library && frame.current) library.open({target, eurlex: action.href, label: action.label}, frame.current);
          else setFailure('The BARK Library is unavailable in this environment.');
        }
        else if (action.action === 'close-library') library?.close();
        else if (action.action === 'saved-requests') { setRecoveryOpen(true); refreshRecovery(); }
        else if (!context.deal_scope || (context.deal_scope.root_contract_id === action.root_contract_id && context.deal_scope.can_manage_sharing)) setSharingRoot(action.root_contract_id);
      },
      onReady: () => { window.clearTimeout(connectionTimer.current); setFailure(null); setReady(true); }, onError: setFailure,
      onDisconnect: () => {
        setReady(false); setFailure(null); window.clearTimeout(connectionTimer.current);
        connectionTimer.current = window.setTimeout(() => setFailure('The Classification workspace did not connect. Try again.'), 20000);
      },
      onSelection: (next, replace) => {
        actual.current = next;
        if (latestSelection.current !== next) {
          latestSelection.current = next;
          navigate(classificationParentPath(context, next), { replace });
        }
      },
    });
    bridge.current = detach;
    element.src = classificationDocument(base, latestSelection.current).href;
    connectionTimer.current = window.setTimeout(() => setFailure('The Classification workspace did not connect. Try again.'), 20000);
    return () => { window.clearTimeout(connectionTimer.current); detach(); element.src = 'about:blank'; };
  }, [base, context, projectId, navigate, attempt, library, onAgentWidth]);
  React.useEffect(() => {
    if (ready) window.clearTimeout(connectionTimer.current);
  }, [ready]);
  React.useEffect(() => {
    if (frame.current && actual.current !== null && actual.current !== selection) {
      setReady(false); setFailure(null);
      window.clearTimeout(connectionTimer.current);
      connectionTimer.current = window.setTimeout(() => setFailure('The Classification workspace did not connect. Try again.'), 20000);
      frame.current.src = classificationDocument(base, selection).href;
    }
  }, [base, selection]);
  return <div className="h-full flex-1 min-h-0 flex flex-col relative">
    {sharingRoot && <ShareDealDialog key={sharingRoot} context={context} rootContractId={sharingRoot} onClose={() => setSharingRoot(undefined)} />}
    {(recoveryOpen || savedRequests.length > 0 || recoveryMessage) && <aside aria-label="Saved request recovery" className="absolute bottom-4 right-4 z-20 max-w-lg rounded-md border border-border-default bg-surface p-3 text-sm text-fg shadow-lg">
      <button className="text-accent underline" aria-expanded={recoveryOpen} onClick={() => setRecoveryOpen(value => !value)}>Saved requests{savedRequests.length ? ` (${savedRequests.length})` : ''}</button>
      {recoveryOpen && <div>
      <button className="text-accent underline ml-3" disabled={recoveryBusy} onClick={refreshRecovery}>Refresh saved requests</button>
      {!savedRequests.length && !recoveryMessage && <p>No saved requests need attention.</p>}
      {recoveryMessage && <p role="status">{recoveryMessage} <button className="text-accent underline" disabled={recoveryBusy} onClick={retry}>Reload saved view</button></p>}
      {savedRequests.map(saved => <div key={saved.command_id} className="flex gap-3 items-center py-1">
        <span>{['completed', 'rejected'].includes(saved.phase) ? 'Saved request outcome available' : 'Unfinished saved request'}</span>
        <button className="text-accent underline" disabled={recoveryBusy} onClick={() => recover(saved, false)}>Check saved outcome</button>
        {(saved.can_stop || saved.can_abandon) && <button className="text-accent underline" disabled={recoveryBusy} onClick={() => recover(saved, true)}>Stop saved request</button>}
      </div>)}
      </div>}
    </aside>}
    {failure && <div role="alert" className="p-3 border-b border-danger bg-danger-bg text-danger text-sm">
      {failure} <button className="text-accent underline" onClick={retry}>Retry</button>
    </div>}
    {!ready && !failure && <p role="status" className="p-6 text-sm text-fg-secondary">Opening Classification workspace…</p>}
    <iframe ref={frame} title="Classification workspace" className="flex-1 w-full border-0 min-h-0"
      sandbox="allow-scripts allow-same-origin allow-forms allow-downloads allow-popups" referrerPolicy="origin" />
  </div>;
}
